import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { AlertCircle, ChevronDown, ChevronUp, ChevronsUpDown, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TopTabs } from '@/components/dashboard/TopTabs'
import { VariantCountBadge } from '@/components/adjustments/AdjustmentBadges'
import { NewAdjustmentDialog } from '@/components/adjustments/NewAdjustmentDialog'
import {
  OPEN_ADJUSTMENT_STATUSES,
  useAdjustmentList,
  type AdjustmentSummary,
} from '@/hooks/useAdjustments'
import { fmtDateTime } from '@/lib/dates'
import { cn } from '@/lib/utils'

type Tab = 'open' | 'archived'

const TABS: { id: Tab; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'archived', label: 'Archived' },
]

const OPEN = new Set<string>(OPEN_ADJUSTMENT_STATUSES)

function TableSkeleton() {
  return (
    <div className="p-4 space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  )
}

const COLUMNS: ColumnDef<AdjustmentSummary>[] = [
  {
    accessorKey: 'adjustment_id',
    id: 'adjustment_id',
    header: 'No.',
    size: 80,
    cell: ({ row }) => (
      <span className="text-xs tabular-nums text-[var(--ink-2)]">
        #{row.original.adjustment_id}
      </span>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'reason',
    id: 'reason',
    header: 'Reason',
    size: 200,
    // Blank for stocktake-generated records — Stocky leaves the cell empty and
    // staff read that emptiness as "this came from a count".
    cell: ({ row }) => (
      <span className="text-xs text-[var(--text-primary)]">{row.original.reason || '—'}</span>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'notes',
    id: 'notes',
    header: 'Notes',
    cell: ({ row }) => (
      <span className="text-xs text-[var(--ink-2)]">{row.original.notes || '—'}</span>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'adjusted_by',
    id: 'adjusted_by',
    header: 'Adjusted By',
    size: 140,
    cell: ({ row }) => (
      <span className="text-xs text-[var(--ink-2)]">{row.original.adjusted_by || '—'}</span>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'adjusted_at',
    id: 'adjusted_at',
    header: 'Adjusted At',
    size: 170,
    // Empty on a draft: nothing has been pushed, so there is no moment to stamp.
    cell: ({ row }) => (
      <span className="text-xs tabular-nums whitespace-nowrap text-[var(--mute)]">
        {fmtDateTime(row.original.adjusted_at) || '—'}
      </span>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'variant_count',
    id: 'variant_count',
    header: 'Variants',
    size: 100,
    cell: ({ row }) => <VariantCountBadge count={row.original.variant_count} />,
    meta: { numeric: true },
    enableSorting: true,
  },
]

export default function Adjustments() {
  const navigate = useNavigate()
  const [newOpen, setNewOpen] = React.useState(false)
  const [tab, setTab] = React.useState<Tab>('open')
  // One fetch, split client-side — the same idiom as the Stocktakes list, and
  // there are only two statuses between the two tabs.
  const { data: adjustments = [], isLoading, isError, refetch } = useAdjustmentList()
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'adjustment_id', desc: true },
  ])

  const openCount = adjustments.filter((a) => OPEN.has(a.status)).length
  const rows = React.useMemo(
    () =>
      adjustments.filter((a) => (tab === 'open' ? OPEN.has(a.status) : !OPEN.has(a.status))),
    [adjustments, tab]
  )

  const table = useReactTable({
    data: rows,
    columns: COLUMNS,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className="brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />
      {/* Page header */}
      <div className="flex items-center gap-3 border-b border-[var(--line-warm)] px-5 py-3.5 bg-[var(--surface-card)]">
        <h1 className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">
          Adjustments
        </h1>
        <div className="flex-1" />
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={() => setNewOpen(true)}
          data-testid="new-adjustment-btn"
        >
          Create Adjustment
        </Button>
      </div>

      {/* Open / Archived */}
      <div className="flex h-9 items-stretch border-b border-[var(--line-warm)] bg-[var(--surface-card)]">
        {TABS.map((t) => {
          const active = tab === t.id
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'flex items-center gap-1.5 px-3.5 text-xs border-r border-[var(--line-warm)] transition-colors',
                active
                  ? 'text-[var(--text-primary)] font-semibold'
                  : 'text-[var(--ink-2)] hover:text-[var(--text-primary)]'
              )}
              style={active ? { boxShadow: 'inset 0 -2px 0 var(--text-primary)' } : undefined}
              data-testid={`adjustment-tab-${t.id}`}
            >
              {t.label}
              <span className="text-[10px] text-[var(--mute)]">
                {t.id === 'open' ? openCount : adjustments.length - openCount}
              </span>
            </button>
          )
        })}
      </div>

      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {isLoading && <TableSkeleton />}

        {isError && !isLoading && (
          <div className="flex justify-center py-12">
            <Card className="w-80 rounded-none shadow-none border-[var(--line-warm)] bg-[var(--surface-card)]">
              <CardContent className="flex flex-col items-center gap-3 pt-6 pb-5">
                <AlertCircle className="h-8 w-8 text-[var(--status-rejected-fg)]" />
                <p className="text-sm text-[var(--ink-2)] text-center">
                  Failed to load adjustments
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={() => refetch()}
                >
                  <RefreshCw className="mr-1.5 h-3 w-3" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {!isLoading && !isError && rows.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <p className="text-sm text-[var(--ink-2)]">
              {tab === 'open' ? 'No open adjustments' : 'No performed adjustments'}
            </p>
            {tab === 'open' && (
              <Button
                size="sm"
                className="mt-2 h-7 text-xs"
                onClick={() => setNewOpen(true)}
              >
                Create Adjustment
              </Button>
            )}
          </div>
        )}

        {!isLoading && !isError && rows.length > 0 && (
          <div className="overflow-auto flex-1">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((hg) => (
                  <TableRow key={hg.id} className="hover:bg-transparent">
                    {hg.headers.map((header) => {
                      const numeric =
                        (header.column.columnDef.meta as { numeric?: boolean } | undefined)
                          ?.numeric ?? false
                      return (
                      <TableHead
                        key={header.id}
                        className={cn(
                          'sticky top-0 z-10',
                          numeric && 'text-right',
                          header.column.getCanSort() && 'cursor-pointer select-none'
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <span
                          className={cn(
                            'inline-flex items-center gap-1',
                            numeric && 'justify-end w-full'
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {header.column.getCanSort() && (
                            <span className="opacity-50">
                              {header.column.getIsSorted() === 'asc' ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ChevronDown className="h-3 w-3" />
                              ) : (
                                <ChevronsUpDown className="h-3 w-3" />
                              )}
                            </span>
                          )}
                        </span>
                      </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    className={cn(
                      'cursor-pointer border-b border-[var(--line-warm)]',
                      index % 2 === 1
                        ? 'bg-[var(--surface-zebra)]'
                        : 'bg-[var(--surface-card)]'
                    )}
                    // The whole row is the target — opening the adjustment is the
                    // one thing you do with it.
                    onClick={() => navigate(`/adjustments/${row.original.adjustment_id}`)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        navigate(`/adjustments/${row.original.adjustment_id}`)
                      }
                    }}
                    aria-label={`Open adjustment ${row.original.adjustment_id}`}
                    data-testid={`adjustment-row-${row.original.adjustment_id}`}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const numeric =
                        (cell.column.columnDef.meta as { numeric?: boolean } | undefined)
                          ?.numeric ?? false
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn('py-1.5 px-2 text-xs', numeric && 'text-right tabular-nums')}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <NewAdjustmentDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  )
}
